import axios from 'axios';
import toast from 'react-hot-toast';

// API Configuration - use relative URL in production, localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`API Request: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
    
    return response;
  },
  (error) => {
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/auth/login';
      return Promise.reject(error);
    }
    
    if (error.response?.status === 403) {
      toast.error('Access denied. You do not have permission to perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('Resource not found.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }
    
    return Promise.reject(error);
  }
);

// API Service Functions
export const projectAPI = {
  // Get all projects
  getAll: async () => {
    const response = await api.get('/api/projects');
    return response.data;
  },

  getProjects: async () => {
    const response = await api.get('/api/projects');
    return response.data;
  },

  // Get project by ID
  getProject: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}`);
    return response.data;
  },

  // Create new project
  createProject: async (projectData) => {
    const response = await api.post('/api/projects', projectData);
    return response.data;
  },

  // Update project
  updateProject: async (projectId, projectData) => {
    const response = await api.put(`/api/projects/${projectId}`, projectData);
    return response.data;
  },

  // Delete project
  deleteProject: async (projectId) => {
    const response = await api.delete(`/api/projects/${projectId}`);
    return response.data;
  },

  // Query project (AI chat)
  queryProject: async (projectId, question) => {
    const response = await api.post(`/api/projects/${projectId}/query`, { question });
    return response.data;
  },

  // Get project analytics
  getProjectAnalytics: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}/analytics`);
    return response.data;
  },

  // Get AI analysis for project
  getAIAnalysis: async (projectId) => {
    const response = await api.post(`/api/projects/${projectId}/ai-analysis`);
    return response.data;
  },

  // Get AI insights for project
  getAIInsights: async (projectId) => {
    const response = await api.get(`/api/projects/${projectId}/ai-insights`);
    return response.data;
  },

};

export const analyticsAPI = {
  // Get dashboard analytics
  getDashboard: async () => {
    const response = await api.get('/api/analytics/dashboard');
    return response.data;
  },

  // Get project performance metrics
  getProjectMetrics: async (timeRange = '30d') => {
    const response = await api.get(`/api/analytics/projects?range=${timeRange}`);
    return response.data;
  },

  // Get team performance metrics
  getTeamMetrics: async (timeRange = '30d') => {
    const response = await api.get(`/api/analytics/team?range=${timeRange}`);
    return response.data;
  },

  // Get resource utilization
  getResourceUtilization: async () => {
    const response = await api.get('/api/analytics/resources');
    return response.data;
  },

  // Get timeline analytics
  getTimeline: async () => {
    const response = await api.get('/api/analytics/timeline');
    return response.data;
  },

  // Get success probability distribution
  getSuccessProbability: async () => {
    const response = await api.get('/api/analytics/success-probability');
    return response.data;
  },

  // Get budget analytics
  getBudget: async () => {
    const response = await api.get('/api/analytics/budget');
    return response.data;
  },

  // Get team workload analytics
  getTeamWorkload: async () => {
    const response = await api.get('/api/analytics/team-workload');
    return response.data;
  },
};

export const settingsAPI = {
  // Get organization configuration
  getOrganization: async () => {
    const response = await api.get('/api/setup/organization');
    return response.data;
  },

  // Save organization configuration
  saveOrganization: async (config) => {
    const response = await api.post('/api/setup/organization', config);
    return response.data;
  },
};

export const searchAPI = {
  // Global search
  search: async (query, limit = 20) => {
    const response = await api.get('/api/search', { params: { q: query, limit } });
    return response.data;
  },

  // Quick search for instant results
  quickSearch: async (query) => {
    const response = await api.get('/api/search/quick', { params: { q: query } });
    return response.data;
  },

  // Autocomplete suggestions
  autocomplete: async (query, limit = 10) => {
    const response = await api.get('/api/search/autocomplete', { params: { q: query, limit } });
    return response.data;
  },
};

export const realIntegrationsAPI = {
  // Slack
  sendSlackNotification: async (message, channel = null, attachments = null) => {
    const response = await api.post('/api/integrations/slack/notify', {
      message,
      channel,
      attachments
    });
    return response.data;
  },
  
  getSlackConfig: async () => {
    const response = await api.get('/api/integrations/slack/config');
    return response.data;
  },
  
  // GitHub
  createGitHubIssue: async (title, body, labels = [], assignees = []) => {
    const response = await api.post('/api/integrations/github/issue', {
      title,
      body,
      labels,
      assignees
    });
    return response.data;
  },
  
  getGitHubConfig: async () => {
    const response = await api.get('/api/integrations/github/config');
    return response.data;
  },
  
  // Email
  sendEmail: async (to_emails, subject, body, html_body = null) => {
    const response = await api.post('/api/integrations/email/send', {
      to_emails,
      subject,
      body,
      html_body
    });
    return response.data;
  },
  
  getEmailConfig: async () => {
    const response = await api.get('/api/integrations/email/config');
    return response.data;
  },
  
  // Webhook
  triggerWebhook: async (event_type, payload) => {
    const response = await api.post('/api/integrations/webhook/trigger', {
      event_type,
      payload
    });
    return response.data;
  },
};

export const hitlAPI = {
  // Get pending approvals
  getPendingApprovals: async () => {
    const response = await api.get('/api/hitl/pending');
    return response.data;
  },
  
  // Create checkpoint
  createCheckpoint: async (checkpointData) => {
    const response = await api.post('/api/hitl/checkpoints', checkpointData);
    return response.data;
  },
  
  // Get project checkpoints
  getProjectCheckpoints: async (projectId) => {
    const response = await api.get(`/api/hitl/checkpoints/${projectId}`);
    return response.data;
  },
  
  // Approve checkpoint
  approveCheckpoint: async (checkpointId, comments, feedback = {}) => {
    const response = await api.post(`/api/hitl/checkpoints/${checkpointId}/approve`, {
      comments,
      feedback
    });
    return response.data;
  },
  
  // Reject checkpoint
  rejectCheckpoint: async (checkpointId, reason, feedback = {}) => {
    const response = await api.post(`/api/hitl/checkpoints/${checkpointId}/reject`, {
      reason,
      feedback
    });
    return response.data;
  },
  
  // Create intervention
  createIntervention: async (interventionData) => {
    const response = await api.post('/api/hitl/interventions', interventionData);
    return response.data;
  },
  
  // Get project interventions
  getProjectInterventions: async (projectId) => {
    const response = await api.get(`/api/hitl/interventions/${projectId}`);
    return response.data;
  },
  
  // Resolve intervention
  resolveIntervention: async (interventionId, resolution, actions_taken = []) => {
    const response = await api.post(`/api/hitl/interventions/${interventionId}/resolve`, {
      resolution,
      actions_taken
    });
    return response.data;
  },
  
  // Validate project intent
  validateIntent: async (projectId, original_goal, ai_interpretation) => {
    const response = await api.post('/api/hitl/validate-intent', {
      project_id: projectId,
      original_goal,
      ai_interpretation
    });
    return response.data;
  },
  
  // Create quality gate
  createQualityGate: async (projectId, milestoneId, quality_criteria, reviewers) => {
    const response = await api.post('/api/hitl/quality-gate', {
      project_id: projectId,
      milestone_id: milestoneId,
      quality_criteria,
      reviewers
    });
    return response.data;
  },
};

export const teamAPI = {
  // Get all team members (alias for getTeamMembers)
  getAll: async () => {
    const response = await api.get('/api/team');
    return response.data;
  },

  // Get team members
  getTeamMembers: async () => {
    const response = await api.get('/api/team');
    return response.data;
  },

  // Get team member details
  getTeamMember: async (memberId) => {
    const response = await api.get(`/api/team/${memberId}`);
    return response.data;
  },

  // Update team member
  updateTeamMember: async (memberId, memberData) => {
    const response = await api.put(`/api/team/members/${memberId}`, memberData);
    return response.data;
  },

  // Get team member workload
  getTeamMemberWorkload: async (memberId) => {
    const response = await api.get(`/api/team/${memberId}/workload`);
    return response.data;
  },

  // Get team member projects
  getTeamMemberProjects: async (memberId) => {
    const response = await api.get(`/api/team/${memberId}/projects`);
    return response.data;
  },
};

export const domainAPI = {
  // Software Development Templates
  getTemplates: async () => {
    const response = await api.get('/api/domain/software-dev/templates');
    return response.data;
  },
  
  generateProject: async (templateName, projectName, teamSize, timelineWeeks, additionalRequirements) => {
    const response = await api.post('/api/domain/software-dev/generate-project', null, {
      params: {
        template_name: templateName,
        project_name: projectName,
        team_size: teamSize,
        timeline_weeks: timelineWeeks,
        additional_requirements: additionalRequirements
      }
    });
    return response.data;
  },
  
  generateSprintPlan: async (sprintNumber, teamVelocity, backlogItems, teamCapacity) => {
    const response = await api.post('/api/domain/software-dev/sprint-plan', {
      sprint_number: sprintNumber,
      team_velocity: teamVelocity,
      backlog_items: backlogItems,
      team_capacity: teamCapacity
    });
    return response.data;
  },
  
  getCodeReviewChecklist: async (language = 'python') => {
    const response = await api.get('/api/domain/software-dev/code-review-checklist', {
      params: { language }
    });
    return response.data;
  },
  
  getBestPractices: async () => {
    const response = await api.get('/api/domain/software-dev/best-practices');
    return response.data;
  },
  
  getWorkflows: async () => {
    const response = await api.get('/api/domain/software-dev/workflows');
    return response.data;
  }
};

export const learningAPI = {
  // Get learning insights
  getInsights: async () => {
    const response = await api.get('/api/learning/insights');
    return response.data;
  },
  
  // Get improved estimate
  getImprovedEstimate: async (base_estimate, project_type, team_members, complexity) => {
    const response = await api.post('/api/learning/estimate', {
      base_estimate,
      project_type,
      team_members,
      complexity
    });
    return response.data;
  },
  
  // Get team recommendations
  getTeamRecommendations: async (project_type, required_skills, project_duration, available_members) => {
    const response = await api.post('/api/learning/team-recommendations', {
      project_type,
      required_skills,
      project_duration,
      available_members
    });
    return response.data;
  },
  
  // Record project outcome
  recordOutcome: async (outcomeData) => {
    const response = await api.post('/api/learning/record-outcome', outcomeData);
    return response.data;
  },
  
  // Get team member performance
  getTeamMemberPerformance: async (member_id) => {
    const response = await api.get(`/api/learning/team-performance/${member_id}`);
    return response.data;
  },
};

export const evmAPI = {
  // Get project EVM metrics
  getProjectEVM: async (project_id, measurement_date = null) => {
    const params = measurement_date ? { measurement_date } : {};
    const response = await api.get(`/api/evm/project/${project_id}`, { params });
    return response.data;
  },
  
  // Get EVM dashboard for all projects
  getEVMDashboard: async () => {
    const response = await api.get('/api/evm/dashboard');
    return response.data;
  },
};

export const onboardingAPI = {
  // Start onboarding
  startOnboarding: async (userId) => {
    const response = await api.post('/api/onboarding/start', { user_id: userId });
    return response.data;
  },

  // Complete onboarding step
  completeStep: async (userId, stepId, timeSpent, feedbackScore) => {
    const response = await api.post('/api/onboarding/complete-step', {
      user_id: userId,
      step_id: stepId,
      time_spent: timeSpent,
      feedback_score: feedbackScore,
    });
    return response.data;
  },

  // Request help
  requestHelp: async (userId, stepId, issueDescription) => {
    const response = await api.post('/api/onboarding/help', {
      user_id: userId,
      step_id: stepId,
      issue_description: issueDescription,
    });
    return response.data;
  },

  // Get onboarding analytics
  getAnalytics: async () => {
    const response = await api.get('/api/onboarding/analytics');
    return response.data;
  },
};

export const authAPI = {
  // Login
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await api.post('/api/auth/logout');
    localStorage.removeItem('auth_token');
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  // Refresh token
  refreshToken: async () => {
    const response = await api.post('/api/auth/refresh');
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  // Send chat message
  sendMessage: async (data) => {
    const response = await api.post('/api/chat/message', data);
    return response.data;
  },
};

// Documents API
export const documentsAPI = {
  // List all documents
  list: async () => {
    const response = await api.get('/api/documents');
    return response.data.documents;
  },

  // Upload documents
  upload: async (formData) => {
    const response = await api.post('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Download document
  download: async (documentId, filename) => {
    const response = await api.get(`/api/documents/${documentId}`, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Delete document
  delete: async (documentId) => {
    const response = await api.delete(`/api/documents/${documentId}`);
    return response.data;
  },
};

// Integrations API
export const integrationsAPI = {
  // List all integrations
  list: async () => {
    const response = await api.get('/api/integrations');
    return response.data.integrations || response.data;
  },

  // Add new integration
  add: async (data) => {
    const response = await api.post('/api/integrations', data);
    return response.data;
  },

  // Toggle integration
  toggle: async (id, enabled) => {
    const response = await api.post('/api/integrations/toggle', { id, enabled });
    return response.data;
  },

  // Delete integration
  delete: async (id) => {
    const response = await api.delete(`/api/integrations/${id}`);
    return response.data;
  },

  // Test integration
  test: async (id) => {
    const response = await api.post(`/api/integrations/${id}/test`);
    return response.data;
  },

  // Get integration status
  getStatus: async () => {
    const response = await api.get('/api/integrations/status');
    return response.data;
  },
};

// Task API
export const taskAPI = {
  // Get all tasks for a project
  getProjectTasks: async (projectId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
    
    const response = await api.get(`/api/projects/${projectId}/tasks?${params.toString()}`);
    return response.data;
  },

  // Get single task
  getTask: async (taskId) => {
    const response = await api.get(`/api/tasks/${taskId}`);
    return response.data;
  },

  // Create task
  createTask: async (projectId, taskData) => {
    const response = await api.post(`/api/projects/${projectId}/tasks`, taskData);
    return response.data;
  },

  // Update task
  updateTask: async (taskId, taskData) => {
    const response = await api.put(`/api/tasks/${taskId}`, taskData);
    return response.data;
  },

  // Delete task
  deleteTask: async (taskId) => {
    const response = await api.delete(`/api/tasks/${taskId}`);
    return response.data;
  },

  // Generate tasks from AI
  generateTasksFromAI: async (projectId) => {
    const response = await api.post(`/api/projects/${projectId}/tasks/generate-from-insights`);
    return response.data;
  },
};

// Health check
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Export the axios instance for custom requests
export default api;
